import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";
import { Mic, Paperclip, Send, Loader2, Sparkles, ChevronDown, AlertCircle, Briefcase, FileText, X, Zap, LayoutDashboard, BookOpen, CheckCircle } from "lucide-react";
import Layout from "../../components/layout/Layout";
import { useAuth } from "../../context/AuthContext";
import TaskDraftCard from "../../components/ui/TaskDraftCard";
import TaskDrawer from "../../components/ui/TaskDrawer";
import DocumentAnalyzer from "../../components/ui/DocumentAnalyzer";

const RAW_PROMPT_KEY = "obstuchim_raw_prompt";
const RAW_CONTEXT_KEY = "obstuchim_raw_context";
const ACTIVE_SUMMARY_KEY = "obstuchim_active_summary";

export default function AiWorkspace() {
  const { profile } = useAuth();
  const [taskText, setTaskText] = useState(() => localStorage.getItem(RAW_PROMPT_KEY) || "");
  const [extraContext, setExtraContext] = useState(() => localStorage.getItem(RAW_CONTEXT_KEY) || "");
  const [showBriefPanel, setShowBriefPanel] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isAiProcessing, setIsAiProcessing] = useState(false);
  const [aiSummary, setAiSummary] = useState(() => localStorage.getItem(ACTIVE_SUMMARY_KEY) || null);
  const [localSaveStatus, setLocalSaveStatus] = useState("saved"); // 'saving', 'saved'
  const textTimeoutRef = useRef(null);
  
  // Дашборд
  const [drafts, setDrafts] = useState([]); // Pending drafts (if needed)
  const [allDrafts, setAllDrafts] = useState({}); // To keep drafts in memory even if approved
  const [approvedDraftIds, setApprovedDraftIds] = useState(new Set());
  const [deletedDraftIds, setDeletedDraftIds] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDraftId, setSelectedDraftId] = useState(null);

  // Чат-лента
  const [messages, setMessages] = useState([
    {
      id: "welcome",
      role: "ai",
      text: "Привет! Я ваш AI-копайлот. Чем займемся сегодня? Опишите задачу, загрузите бриф или просто задайте вопрос."
    }
  ]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, allDrafts, approvedDraftIds, deletedDraftIds]);

  // Sync text to local storage to prevent data loss on refresh
  useEffect(() => {
    setLocalSaveStatus("saving");
    if (textTimeoutRef.current) clearTimeout(textTimeoutRef.current);
    
    textTimeoutRef.current = setTimeout(() => {
      localStorage.setItem(RAW_PROMPT_KEY, taskText);
      localStorage.setItem(RAW_CONTEXT_KEY, extraContext);
      setLocalSaveStatus("saved");
    }, 500);
    
    return () => clearTimeout(textTimeoutRef.current);
  }, [taskText, extraContext]);

  const mergeDraftsToMemory = (draftsList) => {
    setAllDrafts(prev => {
      const next = { ...prev };
      draftsList.forEach(d => {
        if (!next[d.id]) next[d.id] = d;
      });
      return next;
    });
  };

  // Fetch drafts
  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("obstuchim_token");
      
      const headers = { "Authorization": `Bearer ${token}` };
      
      const draftsRes = await fetch("/api/drafts", { headers });
      
      let initialDraftsData = [];
      if (draftsRes.ok) {
        initialDraftsData = await draftsRes.json();
        setDrafts(initialDraftsData);
        mergeDraftsToMemory(initialDraftsData);
      }
      
      // Load current conversation if it exists
      const conversationId = localStorage.getItem("obstuchim_conversation_id");
      if (conversationId) {
        const convRes = await fetch(`/api/ai/copilot/conversations/${conversationId}`, { headers });
        if (convRes.ok) {
          const convData = await convRes.json();
          if (convData && convData.messages && convData.messages.length > 0) {
            setMessages(convData.messages.map(m => {
              let suggestedExperts;
              if (m.toolCallsJson) {
                try {
                  const tools = typeof m.toolCallsJson === 'string' ? JSON.parse(m.toolCallsJson) : m.toolCallsJson;
                  const expertTool = tools.find(t => t.name === 'suggest_expert');
                  // We don't have the rich expert data in toolCallsJson, but we can reconstruct a basic one or let the backend return it.
                  // For now we'll just parse draftIds properly.
                } catch (e) {}
              }
              
              return {
                id: m.id || m.createdAt || Date.now().toString() + Math.random(),
                role: m.role.toLowerCase(),
                text: m.content || "Без текста",
                context: null,
                draftIds: m.role === 'ASSISTANT' && initialDraftsData.length > 0 && m.id === convData.messages[convData.messages.length - 1].id ? initialDraftsData.map(d => d.id) : undefined
              };
            }));
            setIsLoading(false);
            return initialDraftsData;
          }
        }
      }
      
      // Если есть черновики при загрузке, выводим их
      if (initialDraftsData.length > 0 && messages.length <= 1) {
        setMessages(prev => {
          if (prev.find(m => m.id === "initial-drafts")) return prev;
          return [...prev, {
            id: "initial-drafts",
            role: "ai",
            text: "У вас остались необработанные черновики с прошлой сессии:",
            draftIds: initialDraftsData.map(d => d.id)
          }];
        });
      }
      
      return initialDraftsData;
    } catch (err) {
      console.error("Failed to load dashboard data", err);
    } finally {
      setIsLoading(false);
    }
    return [];
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Заглушка для микрофона
  const handleMicClick = () => {
    setIsRecording(!isRecording);
    if (!isRecording) {
      setTimeout(() => {
        setIsRecording(false);
        setTaskText(prev => prev + (prev ? " " : "") + "Надо подготовить новый договор...");
      }, 2000);
    }
  };

  const handleSendTask = async (e) => {
    e.preventDefault();
    if (!taskText.trim() && !extraContext.trim()) return;
    
    const savedGoal = taskText.trim();
    const savedContext = extraContext.trim();
    
    // Добавляем сообщение пользователя в чат
    const userMessage = {
      id: Date.now().toString(),
      role: "user",
      text: savedGoal || "Отправлен документ/контекст",
      context: savedContext
    };
    
    setMessages(prev => [...prev, userMessage]);
    
    setIsAiProcessing(true);
    setAiSummary(null);
    localStorage.removeItem(ACTIVE_SUMMARY_KEY);
    
    setTaskText("");
    setExtraContext("");
    setShowBriefPanel(false);
    localStorage.removeItem(RAW_PROMPT_KEY);
    localStorage.removeItem(RAW_CONTEXT_KEY);
    
    try {
      const token = localStorage.getItem("obstuchim_token");
      const conversationId = localStorage.getItem("obstuchim_conversation_id") || undefined;
      
      const res = await fetch("/api/ai/copilot/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ 
          message: savedGoal,
          extraContext: savedContext || undefined,
          conversationId
        })
      });
      
      if (res.ok) {
        const copilotResponse = await res.json();
        
        if (copilotResponse.conversationId) {
          localStorage.setItem("obstuchim_conversation_id", copilotResponse.conversationId);
        }
        
        const currentDrafts = await fetchData(); // Fetch to get potentially new drafts generated by the server
        
        // В Этапе 4 мы используем реальный ответ бэкенда
        const recentDraftIds = currentDrafts.filter(d => !allDrafts[d.id]).map(d => d.id);
        const draftIdsToAttach = recentDraftIds.length > 0 ? recentDraftIds : (copilotResponse.createdDraft ? [copilotResponse.createdDraft.id] : undefined);
        
        setMessages(prev => [...prev, {
          id: Date.now().toString() + "-ai",
          role: "ai",
          text: copilotResponse.message || "Я проанализировал ваш запрос.",
          draftIds: draftIdsToAttach,
          suggestedExperts: copilotResponse.suggestedExperts
        }]);
      }
    } catch (err) {
      console.error("Task processing error", err);
      setMessages(prev => [...prev, {
        id: Date.now().toString() + "-error",
        role: "ai",
        text: "Произошла ошибка при обработке запроса. Попробуйте еще раз."
      }]);
    } finally {
      setIsAiProcessing(false);
    }
  };

  const handleApproveDraft = async (draftId) => {
    try {
      const token = localStorage.getItem("obstuchim_token");
      const res = await fetch(`/api/drafts/${draftId}/approve`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        if (selectedDraftId === draftId) {
          setSelectedDraftId(null);
        }
        setApprovedDraftIds(prev => new Set(prev).add(draftId));
        // We do NOT fetch data immediately so the drafts remain in our memory
        // Or if we do, our allDrafts preserves the data
        fetchData(); 
      }
    } catch (err) {
      console.error("Failed to approve draft", err);
    }
  };

  const handleDeleteDraft = async (draftId) => {
    try {
      const token = localStorage.getItem("obstuchim_token");
      const res = await fetch(`/api/drafts/${draftId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        setDeletedDraftIds(prev => new Set(prev).add(draftId));
        if (selectedDraftId === draftId) {
          setSelectedDraftId(null);
        }
      }
    } catch (err) {
      console.error("Failed to delete draft", err);
    }
  };

  const handleUpdateDraftOptimistic = (draftId, updatedDraft) => {
    setAllDrafts(prev => {
      const d = prev[draftId];
      if (!d) return prev;
      return {
        ...prev,
        [draftId]: {
          ...d,
          ...updatedDraft,
          payloadJson: updatedDraft.payloadJson || {
            ...(d.payloadJson || {}),
            ...updatedDraft
          }
        }
      };
    });
  };

  return (
    <Layout>
      <div className="min-h-screen pt-20 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto flex flex-col gap-8">
        
        {/* Хедер & Переключатель контекста */}
        <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-neutral-500 font-extralight text-sm mb-1">Рабочее пространство</p>
            <div className="flex items-center gap-2 cursor-pointer group">
              <div className="w-8 h-8 rounded-md bg-neutral-800 border border-neutral-700 flex items-center justify-center">
                <Briefcase className="w-4 h-4 text-primary-400" />
              </div>
              <h1 className="text-2xl font-light text-neutral-100 group-hover:text-primary-400 transition-colors">
                {profile?.company_name || "Мой первый проект"}
              </h1>
              <ChevronDown className="w-5 h-5 text-neutral-600 group-hover:text-primary-400 transition-colors" />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button className="px-4 py-2 rounded-md bg-neutral-900 border border-neutral-800 text-neutral-300 hover:text-white font-extralight text-sm transition-colors flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-accent-500" />
              Призвать Эксперта
            </button>
          </div>
        </header>

        {/* Navigation Tabs */}
        <div className="flex items-center gap-1 border-b border-neutral-800 pb-px">
          <Link 
            to="/workspace"
            className="px-4 py-2 text-sm font-medium text-primary-400 border-b-2 border-primary-500 flex items-center gap-2"
          >
            <Zap className="w-4 h-4" />
            AI-Копайлот
          </Link>
          <Link 
            to="/workspace/kanban"
            className="px-4 py-2 text-sm font-medium text-neutral-400 hover:text-neutral-200 transition-colors border-b-2 border-transparent flex items-center gap-2"
          >
            <LayoutDashboard className="w-4 h-4" />
            Доска задач
          </Link>
          <Link 
            to="/workspace/lore"
            className="px-4 py-2 text-sm font-medium text-neutral-400 hover:text-neutral-200 transition-colors border-b-2 border-transparent flex items-center gap-2"
          >
            <BookOpen className="w-4 h-4" />
            База знаний
          </Link>
        </div>

        {/* Чат-интерфейс (Thread) */}
        <div className="flex-1 flex flex-col bg-neutral-900/20 border border-neutral-800 rounded-2xl overflow-hidden min-h-[600px] shadow-2xl relative">
          
          {/* Лента сообщений */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
            {messages.map(msg => (
              <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[90%] sm:max-w-[80%] rounded-2xl p-4 sm:p-5 ${msg.role === 'user' ? 'bg-primary-900/20 border border-primary-500/30 text-white rounded-tr-sm' : 'bg-neutral-800/40 border border-neutral-700/50 text-neutral-200 rounded-tl-sm'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {msg.role === 'user' ? (
                      <span className="text-xs text-primary-400 font-medium">Вы</span>
                    ) : (
                      <><Sparkles className="w-4 h-4 text-accent-500" /><span className="text-xs text-accent-400 font-medium">AI-Копайлот</span></>
                    )}
                  </div>
                  <p className="text-[15px] font-light leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                  
                  {/* Контекст (бриф) */}
                  {msg.context && (
                    <div className="mt-3 p-3 rounded-lg bg-neutral-900/50 border border-neutral-800 text-xs font-extralight text-neutral-400">
                      <span className="block mb-1 text-neutral-500 font-medium">Прикрепленный контекст:</span>
                      <div className="max-h-32 overflow-y-auto pr-2">{msg.context}</div>
                    </div>
                  )}

                  {/* Интерактивные виджеты черновиков (Внутри чата) */}
                  {msg.draftIds && msg.draftIds.length > 0 && (
                    <div className="mt-5 space-y-3">
                      <div className="text-xs font-medium text-neutral-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-accent-500" />
                        Предложенные задачи
                      </div>
                      <div className="space-y-3">
                        {msg.draftIds.map(draftId => {
                          if (deletedDraftIds.has(draftId)) return null;
                          const draft = allDrafts[draftId];
                          if (!draft) return null;
                          return (
                            <TaskDraftCard
                              key={draft.id}
                              draft={draft}
                              isApproved={approvedDraftIds.has(draft.id)}
                              onApprove={handleApproveDraft}
                              onDelete={handleDeleteDraft}
                              onEdit={(d) => setSelectedDraftId(d.id)}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Эксперты (Внутри чата) */}
                  {msg.suggestedExperts && msg.suggestedExperts.length > 0 && (
                    <div className="mt-5 space-y-3">
                      <div className="text-xs font-medium text-neutral-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-accent-500" />
                        Рекомендованные эксперты
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {msg.suggestedExperts.map(expert => (
                          <div key={expert.id} className="bg-neutral-900 border border-neutral-800 rounded-lg p-3 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-neutral-800 flex items-center justify-center shrink-0 overflow-hidden">
                              {expert.avatarUrl ? (
                                <img src={expert.avatarUrl} alt={expert.name} className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-neutral-500 font-medium text-sm">{expert.name.charAt(0)}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-neutral-200 truncate">{expert.name}</div>
                              <div className="text-xs text-neutral-500 truncate">{expert.specialization}</div>
                            </div>
                            <Link 
                              to={`/experts/${expert.id}`}
                              className="px-2.5 py-1 text-xs font-medium text-accent-400 bg-accent-500/10 hover:bg-accent-500/20 rounded transition-colors"
                            >
                              Выбрать
                            </Link>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {/* Лоадер ИИ */}
            <AnimatePresence>
              {isAiProcessing && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="flex justify-start"
                >
                  <div className="max-w-[85%] rounded-2xl p-4 bg-neutral-800/40 border border-neutral-700/50 text-neutral-200 rounded-tl-sm">
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-4 h-4 animate-spin text-accent-500" />
                      <span className="text-sm font-extralight text-neutral-400">ИИ анализирует и формирует ответ...</span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            <div ref={messagesEndRef} />
          </div>

          {/* Зона ввода (Умная строка + Загрузка документов) */}
          <div className="p-4 bg-neutral-900 border-t border-neutral-800 space-y-4">
            {/* AI-Анализатор Документов (Компактный) */}
            <DocumentAnalyzer 
              onGenerateTasks={(conclusions) => {
                setTaskText(conclusions);
              }} 
            />

            {/* Command Center - Умная Строка */}
            <div className="bg-neutral-950 border border-neutral-800 rounded-xl p-3 sm:p-4 relative">
              <form onSubmit={handleSendTask} className="relative">
                <AnimatePresence>
                  {showBriefPanel && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden mb-2"
                    >
                      <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-3 relative">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-xs font-extralight text-neutral-400 flex items-center gap-1.5 uppercase tracking-wider">
                            <FileText className="w-3.5 h-3.5 text-accent-500" />
                            Бриф / Доп. контекст
                          </label>
                          <button
                            type="button"
                            onClick={() => setShowBriefPanel(false)}
                            className="text-neutral-500 hover:text-neutral-300 transition-colors p-1"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <textarea
                          value={extraContext}
                          onChange={(e) => setExtraContext(e.target.value)}
                          placeholder="Вставьте текст, переписку или вводные данные..."
                          rows={3}
                          className="w-full bg-transparent border-none text-sm font-extralight text-neutral-300 focus:outline-none focus:ring-0 p-0 placeholder:text-neutral-600 resize-none"
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <textarea
                  value={taskText}
                  onChange={(e) => setTaskText(e.target.value)}
                  placeholder="Опишите задачу или задайте вопрос ИИ-Копайлоту..."
                  rows={taskText.split('\n').length > 1 ? Math.min(taskText.split('\n').length, 5) : 1}
                  className="w-full bg-transparent border-none text-neutral-100 placeholder:text-neutral-600 rounded-lg pl-2 pr-32 py-2 font-extralight focus:outline-none focus:ring-0 resize-none min-h-[44px]"
                  style={{ height: 'auto' }}
                />
                
                {/* Опции ввода (Скрепка, Микрофон, Отправить) */}
                <div className="absolute right-0 bottom-0 flex items-center gap-1 bg-neutral-950 p-1">
                  <button 
                    type="button"
                    onClick={() => setShowBriefPanel(!showBriefPanel)}
                    className={`w-9 h-9 flex items-center justify-center rounded-md transition-colors relative ${showBriefPanel || extraContext.length > 0 ? 'text-accent-500 bg-accent-900/20' : 'text-neutral-500 hover:text-neutral-300 hover:bg-neutral-800'}`}
                    title="Прикрепить контекст"
                  >
                    <Paperclip className="w-4 h-4" />
                    {extraContext.length > 0 && !showBriefPanel && (
                      <span className="absolute top-1 right-1 w-2 h-2 bg-accent-500 rounded-full" />
                    )}
                  </button>
                  <button 
                    type="button"
                    onClick={handleMicClick}
                    className={`w-9 h-9 flex items-center justify-center rounded-md transition-colors relative ${isRecording ? 'text-red-500' : 'text-neutral-500 hover:text-neutral-300 hover:bg-neutral-800'}`}
                  >
                    <Mic className="w-4 h-4" />
                    {isRecording && (
                      <span className="absolute inset-0 rounded-md border border-red-500/50 animate-ping" />
                    )}
                  </button>
                  <div className="w-px h-5 bg-neutral-800 mx-1" />
                  <button 
                    type="submit"
                    disabled={!taskText.trim() && !extraContext.trim() || isAiProcessing}
                    className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-700 hover:bg-primary-600 text-white disabled:opacity-50 transition-colors"
                  >
                    <Send className="w-4 h-4 ml-0.5" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      
      {/* Drawer for Drafts */}
      <TaskDrawer
        task={drafts.find(d => d.id === selectedDraftId) || null}
        isDraft={true}
        isOpen={!!selectedDraftId}
        onClose={() => setSelectedDraftId(null)}
        onUpdate={handleUpdateDraftOptimistic}
        onDelete={handleDeleteDraft}
        onApprove={handleApproveDraft}
      />
    </Layout>
  );
}
